import "src/index.css";

import { useEffect, useState } from "react";

import type { Shift } from "src/api/types";
import { InviteModal } from "src/components/InviteModal";
import { NavMenuPanel, NavMenuToggle, useNavMenu } from "src/components/NavMenu";
import { SSOScreen } from "src/components/SSOScreen";
import { QueueList } from "src/components/QueueList";
import { ShiftPanel } from "src/components/ShiftPanel";
import { Toast } from "src/components/Toast";
import { useDeepLinkParam } from "src/hooks/useDeepLinkParam";
import { useFeatureFlagValue } from "src/hooks/useFeatureFlag";
import { useQueue } from "src/hooks/useQueue";
import { useShift } from "src/hooks/useShift";
import { useToast } from "src/hooks/useToast";

const STORAGE_KEY = "walkin_staff_token";
const PENDING_INVITE_KEY = "walkin:pending_invite_patient_id";

interface Auth {
  token: string;
  email: string;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function loadStoredAuth(): Auth | null {
  const token = localStorage.getItem(STORAGE_KEY);
  if (!token) return null;
  const claims = decodeJwtPayload(token);
  if (!claims) return null;
  if (typeof claims.exp === "number" && claims.exp * 1000 < Date.now()) return null;
  const email = typeof claims.email === "string" ? claims.email : "";
  if (!email) return null;
  return { token, email };
}

interface InvitePrefill {
  patientId: string;
  autoSubmit: boolean;
}

function Dashboard({
  auth,
  onLogout,
  deepLinkPatientId,
  onDeepLinkConsumed,
}: {
  auth: Auth;
  onLogout: () => void;
  deepLinkPatientId: string | null;
  onDeepLinkConsumed: () => void;
}) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitePrefill, setInvitePrefill] = useState<InvitePrefill | null>(null);
  const flagValue = useFeatureFlagValue("ff-se-2971");
  const showInviteMenu =
    flagValue !== null && "boolean" in flagValue && flagValue.boolean === "true";
  const flagResolved = flagValue !== null;
  const { toasts, showToast, dismiss } = useToast();
  const { open: navOpen, toggle: toggleNav } = useNavMenu();
  const { activeShift, setActiveShift, activeShifts, refetchShifts } = useShift(
    auth.token,
    showToast,
  );

  const { entries, loading, error, refetch, lastUpdated } = useQueue(
    auth.token,
    onLogout,
    refetchShifts,
  );

  useEffect(() => {
    if (deepLinkPatientId === null || !flagResolved) return;
    // Consumes a one-shot deep link once the feature flag resolves.
    /* oxlint-disable react-hooks/set-state-in-effect */
    if (showInviteMenu) {
      setInviteOpen(true);
      setInvitePrefill({ patientId: deepLinkPatientId, autoSubmit: true });
    }
    /* oxlint-enable react-hooks/set-state-in-effect */
    onDeepLinkConsumed();
  }, [deepLinkPatientId, flagResolved, showInviteMenu, onDeepLinkConsumed]);

  const activeEntry =
    entries.find((e) => e.status === "in_progress" && e.staff_id === activeShift?.staff_id) ?? null;
  const hasWaitingPatients = entries.some((e) => e.status === "waiting");

  function handleShiftChange(shift: Shift | null) {
    setActiveShift(shift?.status === "active" ? shift : null);
    refetchShifts();
  }

  return (
    <div className="dashboard">
      <Toast toasts={toasts} onDismiss={dismiss} />

      <div className="app-shell">
        {showInviteMenu && (
          <NavMenuPanel
            open={navOpen}
            onInviteClick={() => {
              setInviteOpen(true);
              setInvitePrefill(null);
            }}
          />
        )}

        <div className="app-shell-content">
          <header className="app-header">
            <div className="header-brand">
              {showInviteMenu && <NavMenuToggle open={navOpen} onToggle={toggleNav} />}
              <span className="header-title">Walk-in Dashboard</span>
            </div>
            <div className="header-right">
              <span className="header-email">{auth.email}</span>
              <button className="btn btn-sm btn-outline" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </header>

          {inviteOpen && (
            <InviteModal
              token={auth.token}
              initialPatientId={invitePrefill?.patientId}
              autoSubmit={invitePrefill?.autoSubmit}
              onClose={() => {
                setInviteOpen(false);
                setInvitePrefill(null);
              }}
              onError={showToast}
              onEmailSent={() => showToast("Invitation email sent.", "success")}
            />
          )}

          <main className="dashboard-main">
            <ShiftPanel
              token={auth.token}
              activeShift={activeShift}
              activeShifts={activeShifts}
              ownEmail={auth.email}
              activeEntry={activeEntry}
              hasWaitingPatients={hasWaitingPatients}
              onShiftChange={handleShiftChange}
              onQueueRefetch={refetch}
              onPatientCalled={refetch}
              onError={showToast}
            />

            <div className="staff-card">
              <section className="queue-section">
                <div className="section-header">
                  <h2>Queue</h2>
                  <span className="queue-count">
                    {
                      entries.filter((e) => e.status === "waiting" || e.status === "in_progress")
                        .length
                    }{" "}
                    patients
                  </span>
                </div>
                <QueueList
                  entries={entries}
                  token={auth.token}
                  loading={loading}
                  error={error}
                  onEntryUpdated={refetch}
                  onError={showToast}
                />
              </section>

              {lastUpdated && (
                <div className="last-updated">
                  Last updated:{" "}
                  {lastUpdated.toLocaleString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function savePendingInvitePatientId(id: string): void {
  try {
    localStorage.setItem(PENDING_INVITE_KEY, id);
  } catch {
    // ignore storage access errors (e.g. private browsing)
  }
}

function consumePendingInvitePatientId(): string | null {
  try {
    const id = localStorage.getItem(PENDING_INVITE_KEY);
    if (!id) return null;
    localStorage.removeItem(PENDING_INVITE_KEY);
    return id;
  } catch {
    return null;
  }
}

function App() {
  const dli = useDeepLinkParam("dli");
  const [auth, setAuth] = useState<Auth | null>(loadStoredAuth);
  const [deepLinkPatientId, setDeepLinkPatientId] = useState<string | null>(() =>
    auth && dli ? dli : null,
  );

  // Runs once on mount; auth and dli are only relevant as they were at initial load.
  useEffect(() => {
    if (!auth && dli) savePendingInvitePatientId(dli);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleLogin(token: string, email: string) {
    localStorage.setItem(STORAGE_KEY, token);
    setDeepLinkPatientId(consumePendingInvitePatientId());
    setAuth({ token, email });
  }

  function handleLogout() {
    localStorage.removeItem(STORAGE_KEY);
    window.google?.accounts.id.disableAutoSelect();
    setAuth(null);
  }

  if (!auth) {
    return <SSOScreen onLogin={handleLogin} />;
  }

  return (
    <Dashboard
      auth={auth}
      onLogout={handleLogout}
      deepLinkPatientId={deepLinkPatientId}
      onDeepLinkConsumed={() => setDeepLinkPatientId(null)}
    />
  );
}

export default App;
